import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { clearIdleClock, writeIdleClock } from '@/components/auth/idleClock';
import {
  broadcastSessionLock,
  broadcastSessionLogout,
  broadcastSessionUnlock,
  type SessionLockScope,
} from '@/components/auth/sessionBroadcast';

export type UserRole = 'owner' | 'admin' | 'manager' | 'service_advisor' | 'mechanic' | 'developer';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  roleId?: string;
  permissions: string[];
  tenantId: string;
  tenantName: string;
  avatarUrl?: string;
  forcePasswordChange?: boolean;
}

const AUTH_PATHS = ['/login', '/forgot-password', '/reset-password', '/accept-invitation'];

export const AUTH_PERSIST_STORAGE_KEY = 'staff-portal-auth';

export function newSessionFamilyId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `fam-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

type PersistedAuthSnapshot = {
  accessToken: string | null;
  refreshToken: string | null;
  isLocked: boolean;
  sessionFamilyId: string | null;
};

export function readPersistedAuthSnapshot(): PersistedAuthSnapshot | null {
  try {
    const raw = localStorage.getItem(AUTH_PERSIST_STORAGE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const root = parsed as { state?: unknown };
    const state = root.state === undefined ? parsed : root.state;
    if (typeof state !== 'object' || state === null) return null;
    const record = state as Record<string, unknown>;
    const accessToken = typeof record.accessToken === 'string' ? record.accessToken : null;
    const refreshToken = typeof record.refreshToken === 'string' ? record.refreshToken : null;
    const sessionFamilyId =
      typeof record.sessionFamilyId === 'string' && record.sessionFamilyId !== ''
        ? record.sessionFamilyId
        : null;
    return {
      accessToken,
      refreshToken,
      isLocked: record.isLocked === true,
      sessionFamilyId,
    };
  } catch {
    return null;
  }
}

export type LockSessionOptions = {
  broadcast?: boolean;
};

export function isSafeInternalPath(path: unknown): path is string {
  if (typeof path !== 'string' || path.length === 0) return false;
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//') || path.startsWith('/\\')) return false;
  const pathOnly = path.split(/[?#]/)[0] ?? path;
  if (AUTH_PATHS.includes(pathOnly)) return false;
  return true;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  /**
   * Portal lock overlay (#2214). The user, refresh token and query cache stay;
   * only the access token is dropped. Persisted so a reload still shows the
   * overlay on the same page.
   */
  isLocked: boolean;
  /**
   * Generation of the current lock, persisted so a stale unlock broadcast
   * from a previous occupancy cannot open a later lock of the same family.
   */
  lockGeneration: number;
  /**
   * Stable id for this login's refresh family. Survives token rotation and
   * reload. A later login of the same user gets a new id so a delayed
   * lock/unlock broadcast cannot touch it.
   */
  sessionFamilyId: string | null;
  /**
   * In-memory generation for guarding async auth writes. This deliberately is
   * not persisted: a fresh page load is a new generation even when the session
   * itself is restored from storage.
   */
  sessionEpoch: number;
  redirectAfterLogin: string | null;

  setUser: (user: User | null) => void;
  /** Install credentials for a newly authenticated session and advance its generation. */
  startSession: (accessToken: string, refreshToken: string) => void;
  /** Rotate credentials inside the current session without changing its generation. */
  setTokens: (accessToken: string, refreshToken: string) => void;
  /**
   * Rotate credentials only if `epoch` is still the live generation.
   * Used by in-flight refresh completions that outlived a logout/login.
   */
  applyRotatedTokens: (epoch: number, accessToken: string, refreshToken: string) => void;
  clearAuth: () => void;
  /**
   * Drop the session only if `epoch` is still the live generation.
   * A deferred refresh failure must not sign out a newer login.
   */
  clearAuthIfCurrent: (epoch: number) => void;
  /**
   * Drop the access token and mark the session locked. Refresh token, user
   * and query cache stay. No-ops when already locked with no access token.
   * `broadcast: false` keeps a local overlay without claiming the server
   * locked (lock POST did not succeed).
   */
  lockSession: (options?: LockSessionOptions) => void;
  /**
   * Restore an access token after server proof. Does not start a new family.
   */
  unlockSession: (accessToken: string, refreshToken: string) => void;
  /** Apply a lock broadcast from another tab of the same occupant. */
  adoptRemoteLock: (scope: SessionLockScope) => void;
  /** Apply an unlock broadcast from another tab of the same occupant and generation. */
  adoptRemoteUnlock: (scope: SessionLockScope) => void;
  /**
   * Take over tokens another tab of the same family rotated (#3392). Refresh
   * rotation deletes the old key server-side, so a tab still holding it would
   * get 401 on its next refresh and sign every tab out. Returns true when the
   * in-memory tokens changed.
   */
  adoptSiblingTokens: () => boolean;
  setRedirectAfterLogin: (path: string | null) => void;
  consumeRedirectAfterLogin: () => string | null;
  /** @deprecated Use hasPermission/hasAnyPermission instead */
  hasRole: (roles: UserRole[]) => boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLocked: false,
      lockGeneration: 0,
      sessionFamilyId: null,
      sessionEpoch: 0,
      redirectAfterLogin: null,

      setUser: (user) =>
        set({
          user,
          isAuthenticated: user !== null,
        }),

      startSession: (accessToken, refreshToken) => {
        set((state) => ({
          accessToken,
          refreshToken,
          isLocked: false,
          lockGeneration: 0,
          sessionFamilyId: newSessionFamilyId(),
          sessionEpoch: state.sessionEpoch + 1,
        }));
        // A leftover idle timestamp from the previous occupant must not
        // inherit onto this login (#2212). Reloads restore tokens without
        // calling startSession, so the shared clock stays intact then.
        writeIdleClock(Date.now());
      },

      setTokens: (accessToken, refreshToken) => {
        // A sibling tab's logout must not be undone by an in-flight token
        // rotation writing credentials back over the cleared persist snapshot
        // (#2212). A newer session's epoch is a different generation: refuse
        // to rotate tokens unless this generation is still signed in.
        const state = get();
        if (!state.isAuthenticated) return;
        if (state.isLocked) return;
        set({
          accessToken,
          refreshToken,
        });
      },

      applyRotatedTokens: (epoch, accessToken, refreshToken) => {
        const state = get();
        if (!state.isAuthenticated) return;
        if (state.isLocked) return;
        if (state.sessionEpoch !== epoch) return;
        set({
          accessToken,
          refreshToken,
        });
      },

      lockSession: (options) => {
        const state = get();
        if (!state.isAuthenticated) return;
        if (state.isLocked && state.accessToken === null) return;
        const generation = state.lockGeneration + 1;
        const sessionFamilyId = state.sessionFamilyId ?? newSessionFamilyId();
        const shouldBroadcast = options?.broadcast !== false;
        set({
          accessToken: null,
          isLocked: true,
          lockGeneration: generation,
          sessionFamilyId,
        });
        if (shouldBroadcast && state.user) {
          broadcastSessionLock({
            userId: state.user.id,
            tenantId: state.user.tenantId,
            sessionFamilyId,
            generation,
          });
        }
      },

      unlockSession: (accessToken, refreshToken) => {
        const state = get();
        if (!state.isAuthenticated) return;
        const generation = state.lockGeneration;
        const sessionFamilyId = state.sessionFamilyId ?? newSessionFamilyId();
        set({
          accessToken,
          refreshToken,
          isLocked: false,
          sessionFamilyId,
        });
        writeIdleClock(Date.now());
        if (state.user) {
          broadcastSessionUnlock({
            userId: state.user.id,
            tenantId: state.user.tenantId,
            sessionFamilyId,
            generation,
          });
        }
      },

      adoptRemoteLock: (scope) => {
        const state = get();
        if (!state.isAuthenticated || state.user === null) return;
        if (state.user.id !== scope.userId || state.user.tenantId !== scope.tenantId) return;
        if (state.sessionFamilyId === null || state.sessionFamilyId !== scope.sessionFamilyId) {
          return;
        }
        set({
          accessToken: null,
          isLocked: true,
          lockGeneration: scope.generation,
        });
      },

      adoptRemoteUnlock: (scope) => {
        const state = get();
        if (!state.isAuthenticated || state.user === null || !state.isLocked) return;
        if (state.user.id !== scope.userId || state.user.tenantId !== scope.tenantId) return;
        if (state.sessionFamilyId === null || state.sessionFamilyId !== scope.sessionFamilyId) {
          return;
        }
        if (state.lockGeneration !== scope.generation) return;
        const snapshot = readPersistedAuthSnapshot();
        if (
          snapshot === null ||
          snapshot.sessionFamilyId !== scope.sessionFamilyId ||
          snapshot.isLocked ||
          snapshot.refreshToken === null ||
          snapshot.refreshToken === ''
        ) {
          return;
        }
        set({
          isLocked: false,
          accessToken: snapshot.accessToken,
          refreshToken: snapshot.refreshToken,
        });
        writeIdleClock(Date.now());
      },

      adoptSiblingTokens: () => {
        const state = get();
        if (!state.isAuthenticated || state.isLocked || state.sessionFamilyId === null) {
          return false;
        }
        const snapshot = readPersistedAuthSnapshot();
        if (
          snapshot === null ||
          snapshot.isLocked ||
          snapshot.sessionFamilyId !== state.sessionFamilyId ||
          snapshot.accessToken === null ||
          snapshot.refreshToken === null ||
          snapshot.refreshToken === '' ||
          snapshot.refreshToken === state.refreshToken
        ) {
          return false;
        }
        set({
          accessToken: snapshot.accessToken,
          refreshToken: snapshot.refreshToken,
        });
        return true;
      },

      clearAuthIfCurrent:(epoch) => {
        if (get().sessionEpoch !== epoch) return;
        get().clearAuth();
      },

      clearAuth: () => {
        set((state) => ({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLocked: false,
          lockGeneration: 0,
          sessionFamilyId: null,
          sessionEpoch: state.sessionEpoch + 1,
          redirectAfterLogin: null,
        }));
        clearIdleClock();
        broadcastSessionLogout();
      },

      setRedirectAfterLogin: (path) => {
        if (path === null) {
          set({ redirectAfterLogin: null });
          return;
        }
        if (!isSafeInternalPath(path)) return;
        set({ redirectAfterLogin: path });
      },

      consumeRedirectAfterLogin: () => {
        const { redirectAfterLogin } = get();
        if (redirectAfterLogin === null) return null;
        set({ redirectAfterLogin: null });
        return isSafeInternalPath(redirectAfterLogin) ? redirectAfterLogin : null;
      },

      hasRole: (roles) => {
        const { user } = get();
        if (!user) return false;
        if (user.role === 'developer') return true;
        return roles.includes(user.role as UserRole);
      },

      hasPermission: (permission) => {
        const { user } = get();
        if (!user) return false;
        if (user.role === 'developer') return true;
        const perms = user.permissions ?? [];
        if (perms.length === 0) return false;
        return perms.includes(permission);
      },

      hasAnyPermission: (permissions) => {
        const { user } = get();
        if (!user) return false;
        if (user.role === 'developer') return true;
        const perms = user.permissions ?? [];
        if (perms.length === 0) return false;
        return permissions.some((p) => perms.includes(p));
      },

      hasAllPermissions: (permissions) => {
        const { user } = get();
        if (!user) return false;
        if (user.role === 'developer') return true;
        const perms = user.permissions ?? [];
        if (perms.length === 0) return false;
        return permissions.every((p) => perms.includes(p));
      },
    }),
    {
      name: AUTH_PERSIST_STORAGE_KEY,
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        isLocked: state.isLocked,
        lockGeneration: state.lockGeneration,
        sessionFamilyId: state.sessionFamilyId,
        redirectAfterLogin: state.redirectAfterLogin,
      }),
    }
  )
);

// Keep sibling tabs on the latest rotated refresh token (#3392). `storage`
// fires only in the other tabs, so the writer never reacts to itself.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== AUTH_PERSIST_STORAGE_KEY || event.newValue === null) return;
    useAuthStore.getState().adoptSiblingTokens();
  });
}
